
import { useEffect, useState } from 'react';
import { apiClient, EventLogEntry, EventLogResponse } from '@/lib/api-client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { Download, RefreshCw, Search, X } from 'lucide-react';

const TIME_RANGE_OPTIONS = [
  { label: 'Last 1h', minutes: 60 },
  { label: 'Last 6h', minutes: 360 },
  { label: 'Last 24h', minutes: 1440 },
  { label: 'Last 7d', minutes: 10080 },
];

const SEVERITY_OPTIONS = ['', 'INFO', 'WARNING', 'CRITICAL'];
const OUTCOME_OPTIONS = ['', 'success', 'failure', 'denied', 'rejected'];

interface CategoryOption {
  label: string;
  prefixes: string[];
  description: string;
}

const CATEGORY_OPTIONS: CategoryOption[] = [
  { label: 'All', prefixes: [], description: 'Show all events' },
  { label: 'MCP', prefixes: ['mcp.'], description: 'Tool calls, sessions' },
  { label: 'Auth', prefixes: ['auth.', 'authz.'], description: 'JWT validation, access decisions' },
  { label: 'Token', prefixes: ['token.exchange.', 'xaa.', 'vault.', 'sts.service_account.'], description: 'Token exchanges' },
  { label: 'Admin', prefixes: ['admin.', 'config.'], description: 'Config changes, logins' },
  { label: 'OAuth / DCR', prefixes: ['oauth.', 'dcr.', 'cimd.'], description: 'OAuth flows, DCR, CIMD' },
  { label: 'STS', prefixes: ['sts.'], description: 'STS consent flows' },
  { label: 'System', prefixes: ['db.', 'redis.', 'token.cache.', 'user.token.store.', 'config.port.'], description: 'Infrastructure events' },
];

export const EventLog = () => {
  const [response, setResponse] = useState<EventLogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [startMinutesAgo, setStartMinutesAgo] = useState(60);
  const [eventType, setEventType] = useState('');
  const [agentId, setAgentId] = useState('');
  const [severity, setSeverity] = useState('');
  const [correlationId, setCorrelationId] = useState('');
  const [outcome, setOutcome] = useState('');
  const [category, setCategory] = useState('All');
  const { toast } = useToast();

  const loadEvents = async () => {
    setLoading(true);
    try {
      const params: any = { start_minutes_ago: startMinutesAgo };
      if (eventType) params.event_type = eventType;
      if (agentId) params.agent_id = agentId;
      if (severity) params.severity = severity;
      if (correlationId) params.correlation_id = correlationId;

      const data = await apiClient.getEventLogs(params);
      setResponse(data);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to load event log',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClearFilters = () => {
    setEventType('');
    setAgentId('');
    setSeverity('');
    setCorrelationId('');
    setOutcome('');
    setStartMinutesAgo(60);
    setCategory('All');
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params: any = { start_minutes_ago: startMinutesAgo };
      if (eventType) params.event_type = eventType;
      if (agentId) params.agent_id = agentId;
      if (severity) params.severity = severity;
      if (correlationId) params.correlation_id = correlationId;
      if (outcome) params.outcome = outcome;
      if (activeCategoryPrefixes.length > 0) params.event_type_prefixes = activeCategoryPrefixes.join(',');

      const blob = await apiClient.exportEventLogs(params);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `event-log-${new Date().toISOString().replace(/[:.]/g, '-')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to export event log',
        variant: 'destructive',
      });
    } finally {
      setExporting(false);
    }
  };

  const activeCategoryPrefixes = CATEGORY_OPTIONS.find(c => c.label === category)?.prefixes ?? [];

  const applyCategory = (evts: EventLogEntry[]) => {
    let filtered = evts;
    if (activeCategoryPrefixes.length > 0) {
      filtered = filtered.filter(e => activeCategoryPrefixes.some(p => e.event_type.startsWith(p)));
    }
    if (outcome) {
      filtered = filtered.filter(e => e.outcome.toLowerCase().includes(outcome.toLowerCase()));
    }
    return filtered;
  };

  const formatTimestamp = (ts?: string) => {
    if (!ts) return '—';
    const d = new Date(ts);
    if (isNaN(d.getTime())) return ts;
    return d.toLocaleString();
  };

  const getSeverityBadgeColor = (sev: string) => {
    if (sev === 'CRITICAL') return 'bg-red-100 text-red-800';
    if (sev === 'WARNING') return 'bg-yellow-100 text-yellow-800';
    return 'bg-blue-100 text-blue-800';
  };

  const getOutcomeBadgeColor = (outcome: string) => {
    if (outcome.includes('fail') || outcome.includes('denied') || outcome.includes('reject')) {
      return 'bg-red-100 text-red-800';
    }
    if (outcome.includes('success') || outcome.includes('allow') || outcome.includes('grant')) {
      return 'bg-green-100 text-green-800';
    }
    return 'bg-gray-100 text-gray-800';
  };

  const providerLabel = response?.provider
    ? response.provider === 'cloudwatch'
      ? 'CloudWatch'
      : response.provider === 'loganalytics'
      ? 'Log Analytics'
      : response.provider
    : null;

  const events: EventLogEntry[] = applyCategory(response?.events ?? []);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Event Log</h1>
          <p className="text-gray-600 mt-1">
            Live audit events from {providerLabel ?? 'cloud log provider'}
            {response?.query_time_ms != null && (
              <span className="ml-2 text-xs text-gray-400">({response.query_time_ms}ms)</span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadEvents} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Narrow the event stream</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Category filter */}
          <div className="mb-4">
            <Label>Category</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {CATEGORY_OPTIONS.map((opt) => (
                <Button
                  key={opt.label}
                  size="sm"
                  variant={category === opt.label ? 'default' : 'outline'}
                  onClick={() => setCategory(opt.label)}
                  title={opt.description}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4">
            {/* Time range buttons */}
            <div className="lg:col-span-3">
              <Label>Time Range</Label>
              <div className="flex gap-2 mt-1">
                {TIME_RANGE_OPTIONS.map((opt) => (
                  <Button
                    key={opt.minutes}
                    size="sm"
                    variant={startMinutesAgo === opt.minutes ? 'default' : 'outline'}
                    onClick={() => setStartMinutesAgo(opt.minutes)}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="event-type">Event Type</Label>
              <Input
                id="event-type"
                placeholder="e.g. mcp.tool.call"
                value={eventType}
                onChange={(e) => setEventType(e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="agent-id">Agent ID</Label>
              <Input
                id="agent-id"
                placeholder="e.g. claude-code"
                value={agentId}
                onChange={(e) => setAgentId(e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="severity">Severity</Label>
              <select
                id="severity"
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                {SEVERITY_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s || 'All'}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="outcome">Outcome</Label>
              <select
                id="outcome"
                value={outcome}
                onChange={(e) => setOutcome(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                {OUTCOME_OPTIONS.map((o) => (
                  <option key={o} value={o}>
                    {o || 'All'}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="correlation-id">Correlation ID</Label>
              <Input
                id="correlation-id"
                placeholder="e.g. abc-123"
                value={correlationId}
                onChange={(e) => setCorrelationId(e.target.value)}
              />
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <Button onClick={loadEvents} disabled={loading}>
              <Search className="w-4 h-4 mr-2" />
              Apply
            </Button>
            <Button onClick={handleClearFilters} variant="outline">
              <X className="w-4 h-4 mr-2" />
              Clear
            </Button>
            <Button onClick={handleExport} variant="outline" disabled={exporting}>
              <Download className={`w-4 h-4 mr-2 ${exporting ? 'animate-pulse' : ''}`} />
              Export CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Events ({events.length}
            {category !== 'All' && response?.events && response.events.length !== events.length && (
              <span className="text-gray-400 font-normal text-sm ml-1">
                of {response.events.length}
              </span>
            )}
            )
          </CardTitle>
          <CardDescription>
            {response?.provider === 'none'
              ? response.message
              : `Streaming audit events from ${providerLabel ?? 'log provider'}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading events...</div>
          ) : response?.provider === 'none' ? (
            <div className="text-center py-8 text-gray-500">
              <p className="font-medium">Log provider not configured</p>
              <p className="text-sm mt-1">{response.message}</p>
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No events found for this time range</div>
          ) : (
            <div className="space-y-3">
              {events.map((entry, index) => (
                <div
                  key={index}
                  className="border rounded-lg p-4 hover:bg-gray-50 transition"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      {entry.severity && (
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${getSeverityBadgeColor(entry.severity)}`}
                        >
                          {entry.severity}
                        </span>
                      )}
                      {entry.event_type && (
                        <span className="font-mono text-sm font-medium">{entry.event_type}</span>
                      )}
                      {entry.outcome && (
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${getOutcomeBadgeColor(entry.outcome)}`}
                        >
                          {entry.outcome}
                        </span>
                      )}
                    </div>
                    <span className="text-sm text-gray-500 whitespace-nowrap ml-4">
                      {formatTimestamp(entry.timestamp)}
                    </span>
                  </div>

                  {(entry.agent_id || entry.correlation_id) && (
                    <div className="flex gap-4 text-sm text-gray-600 mb-2 flex-wrap">
                      {entry.agent_id && (
                        <span>Agent: <span className="font-mono">{entry.agent_id}</span></span>
                      )}
                      {entry.correlation_id && (
                        <span
                          className="cursor-pointer hover:text-blue-600"
                          title="Click to filter by this correlation ID"
                          onClick={() => setCorrelationId(entry.correlation_id)}
                        >
                          Correlation: <span className="font-mono">{entry.correlation_id}</span>
                        </span>
                      )}
                    </div>
                  )}

                  <details className="mt-2">
                    <summary className="cursor-pointer text-sm text-blue-600 hover:text-blue-800">
                      View Raw Event
                    </summary>
                    <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-x-auto">
                      {(() => {
                        try {
                          return JSON.stringify(JSON.parse(entry.message), null, 2);
                        } catch {
                          return entry.message;
                        }
                      })()}
                    </pre>
                  </details>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
